import { useRef, useState } from 'react';
import { ModuleListView } from '../modules/ModuleListView';
import { useNotify } from '../../components/notifications';
import { api } from '../../api/client';
import { runLoading } from '../../components/loading';
import { FormTitle } from '../../components/FormTitle';
import { AppToolbar } from '../../components/AppToolbar/AppToolbar';

// ─────────────────────────────────────────────────────────────────────────
// Settings → External Data Import → MCL & SCL Limits
// ─────────────────────────────────────────────────────────────────────────
//   Bulk-imports the lab's internal limits spreadsheet (Lab Method · Lab
//   Analyte · DWV Analyte · MCL/MCLG/SCL/Alert/MRL + units · AnalyteNotes) into
//   dbo.MCLAndSCLLimits, then shows the stored limits in the shared grid.  The
//   Phase-3 limit-check engine reads from the same table.  Reads .xls/.xlsx/.xlsm.
// ─────────────────────────────────────────────────────────────────────────

interface Props { onHome: () => void; }

export default function LimitsPage({ onHome }: Props) {
  const notify = useNotify();
  const [reloadKey, setReloadKey] = useState(0);
  const fileInput = useRef<HTMLInputElement>(null);

  const onPickFile = () => fileInput.current?.click();
  const onFileChosen = async (ev: React.ChangeEvent<HTMLInputElement>) => {
    const file = ev.target.files?.[0];
    ev.target.value = '';
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await runLoading(
        api.post<{ data?: { imported: number; skipped: number; messages: string[] } }>(
          '/api/v1/edi-limits/import', form),
        { blocking: true, label: `Importing ${file.name}…` });
      const d = res.data?.data;
      if (d) {
        notify.success(`Imported ${d.imported} limit(s)${d.skipped ? `, skipped ${d.skipped}` : ''}.`);
        if (d.skipped && d.messages?.[1]) notify.warning(d.messages[1]);
        setReloadKey(k => k + 1);
      }
    } catch (e: unknown) {
      notify.error(
        (e as { response?: { data?: { error?: { message?: string } } } })
          .response?.data?.error?.message
        ?? (e as { message?: string }).message ?? 'Import failed.');
    }
  };


  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--ai-bg)' }}>
      <div style={{ padding: '14px 20px 8px' }}>
        <div style={{ fontSize: 11, color: 'var(--ai-muted)', marginBottom: 4 }}>
          Home &nbsp;›&nbsp; Settings &nbsp;›&nbsp; External Data Import &nbsp;›&nbsp;
          <span style={{ color: 'var(--ai-blue)', fontWeight: 600 }}>MCL &amp; SCL Limits</span>
        </div>
        {/* Shared title (nav icon/colour + load-time) — single source of truth. */}
        <FormTitle pageId="edi-limits" />
        <div style={{ fontSize: 11, color: 'var(--ai-muted)', marginTop: 3 }}>
          Regulatory limits per analyte. Import your internal limits spreadsheet (.xls / .xlsx);
          the limit-check engine compares imported results against these values.
        </div>
        <input ref={fileInput} type="file" accept=".xls,.xlsx,.xlsm" style={{ display: 'none' }} onChange={onFileChosen} />
        {/* Shared action bar — form-specific import action in the left slot. */}
        <div style={{ marginTop: 10 }}>
          <AppToolbar left={
            <button className="ca-btn" onClick={onPickFile}><i className="ti ti-upload" /> Import Limits</button>
          } />
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <ModuleListView
          key={`edi-limits-${reloadKey}`}
          moduleKey="edi-limits"
          group="External Data Import"
          navTitle="MCL & SCL Limits"
          onHome={onHome}
        />
      </div>
    </div>
  );
}
